import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { makeSelectSlotById } from '../../../Modules/Slot/Presentation/selectors';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { Slot } from '../../../Modules/Slot/Model/Slot';

const { Option } = Select;

interface Props {
  open: boolean;
  slotIds: number[];
  onClose: () => void;
  onSubmit: (dto: Partial<Slot>) => Promise<void>;
}

const SlotEditModal: React.FC<Props> = ({ open, slotIds, onClose, onSubmit }) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* 첫 번째 슬롯 정보 */
  const firstSlot = useAppSelector(
    slotIds.length ? makeSelectSlotById(slotIds[0]) : () => undefined,
  );

  /* 셀러 계정 목록 */
  const accounts = useAppSelector(selectAccounts);
  const sellers = accounts
    .filter(a => a.grade === 'SELLER')
    .sort((a, b) => a.username.localeCompare(b.username));

  /* 모달 열릴 때 필드 채우기 */
  useEffect(() => {
    if (open && firstSlot) {
      form.setFieldsValue({
        user: firstSlot.user,
        mid_single: firstSlot.mid_single,
        mid_compare: firstSlot.mid_compare,
        product_url: firstSlot.product_url,
        compare_url: firstSlot.compare_url,
        memo: firstSlot.memo,
      });
    } else if (open) {
      form.resetFields();
    }
  }, [open, firstSlot, form]);

  return (
    <Modal
      open={open}
      title={`${firstSlot?.main_keyword || ''} 슬롯 수정`}
      okText="수정"
      confirmLoading={submitting}
      onCancel={() => { if (!submitting) onClose(); }}
      onOk={async () => {
        if (submitting) return;
        const values = await form.validateFields();
        const dto = { ...values, word_keyword: '-' };
        setSubmitting(true);
        try {
          await onSubmit(dto);
          onClose();
          form.resetFields();
        } finally {
          setSubmitting(false);
        }
      }}
      destroyOnClose
      bodyStyle={isMobile ? { maxHeight: '80vh', overflow: 'auto', overscrollBehavior: 'contain' as any } : undefined}
    >
      <Form form={form} layout="vertical">
        {/* 아이디 */}
        <Form.Item
          name="user"
          label="아이디"
          rules={[{ required: true, message: '셀러를 선택하세요.' }]}
        >
          <Select
            placeholder="아이디 / 이름 검색"
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            getPopupContainer={trigger => trigger.parentElement as HTMLElement}
            listHeight={isMobile ? 180 : 256}
            dropdownStyle={isMobile ? { maxHeight: '40vh', overflow: 'auto', overscrollBehavior: 'contain' as any } : undefined}
            dropdownMatchSelectWidth
          >
            {sellers.map(s => (
              <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                {s.username} ({s.name})
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="mid_single"
          label="단일 MID"
          rules={[{ message: '단일 MID를 입력하세요.' }]}
        >
          <Input maxLength={80} placeholder="단일 MID를 입력해주세요." />
        </Form.Item>

        <Form.Item
          name="mid_compare"
          label="가격비교 MID"
          rules={[{ message: '가격비교 MID를 입력하세요.' }]}
        >
          <Input maxLength={80} placeholder="가격비교 MID를 입력해주세요." />
        </Form.Item>

        <Form.Item
          name="product_url"
          label="상품 URL"
          rules={[
            { message: 'URL을 입력하세요.' },
            { type: 'url', message: '올바른 URL 형식이 아닙니다.' },
          ]}
        >
          <Input placeholder="상품 URL을 입력해주세요." />
        </Form.Item>

        <Form.Item
          name="compare_url"
          label="가격비교 URL"
          rules={[
            { message: 'URL을 입력하세요.' },
            { type: 'url', message: '올바른 URL 형식이 아닙니다.' },
          ]}
        >
          <Input placeholder="가격비교 URL을 입력해주세요." />
        </Form.Item>

        <Form.Item name="memo" label="메모">
          <Input.TextArea rows={3} maxLength={300} placeholder="메모를 입력해주세요." />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SlotEditModal;
